(() => {
  'use strict';
  if (customElements.get('product-offer-countdown')) return;

  // Interpret the merchant's wall-clock time in the chosen IANA time zone,
  // never in the visitor's browser time zone. Reject impossible dates/times.
  function deadlineInZone(date, time, zone) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time)) return NaN;
    const [year, month, day] = date.split('-').map(Number);
    const [hour, minute] = time.split(':').map(Number);
    if (year < 2000 || hour > 23 || minute > 59) return NaN;
    const wall = Date.UTC(year, month - 1, day, hour, minute);
    const check = new Date(wall);
    if (check.getUTCFullYear() !== year || check.getUTCMonth() !== month - 1 || check.getUTCDate() !== day) return NaN;
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
      });
      const localWall = stamp => {
        const parts = {};
        formatter.formatToParts(stamp).forEach(part => { parts[part.type] = part.value; });
        return Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
      };
      // Sample both sides of a potential DST transition. An ambiguous fall-back
      // time uses its first occurrence; a skipped spring-forward time is invalid.
      const candidates = [-86400000, 0, 86400000].map(delta => {
        const sample = wall + delta;
        return wall - (localWall(sample) - sample);
      }).filter(stamp => localWall(stamp) === wall);
      return candidates.length ? Math.min(...candidates) : NaN;
    } catch (_) {
      return NaN;
    }
  }

  class ProductOfferCountdown extends HTMLElement {
    connectedCallback() {
      this.frame = requestAnimationFrame(() => this.initialize());
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      clearTimeout(this.timeout);
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    initialize() {
      if (!this.isConnected) return;
      clearTimeout(this.timeout);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.content = this.querySelector('.product-offer-countdown__content');
      this.notice = this.querySelector('[data-editor-notice]');
      this.timer = this.querySelector('[role="timer"]');
      this.units = {};
      this.querySelectorAll('[data-unit]').forEach(node => { this.units[node.dataset.unit] = node; });
      this.showSeconds = this.dataset.showSeconds === 'true';
      this.deadline = deadlineInZone(this.dataset.endDate || '', this.dataset.endTime || '', this.dataset.timezone || 'America/New_York');
      if (!this.content || !this.timer) return;
      if (!Number.isFinite(this.deadline)) {
        this.hideOrExplain('Offer countdown: enter a valid date (YYYY-MM-DD), time (HH:MM), and time zone. Customers will not see this notice.');
        return;
      }
      const deadlineLabel = new Intl.DateTimeFormat('en-US', {
        timeZone: this.dataset.timezone || 'America/New_York', month: 'short',
        day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short'
      }).format(this.deadline);
      const dateNode = this.querySelector('[data-deadline]');
      if (dateNode) {
        dateNode.dateTime = new Date(this.deadline).toISOString();
        dateNode.textContent = deadlineLabel;
      }
      this.timer.setAttribute('aria-label', `Time remaining for this offer. Ends ${deadlineLabel}.`);
      this.content.hidden = false;
      if (this.notice) this.notice.hidden = true;
      this.onVisibility = () => this.tick();
      document.addEventListener('visibilitychange', this.onVisibility);
      this.tick();
    }

    hideOrExplain(message) {
      clearTimeout(this.timeout);
      document.removeEventListener('visibilitychange', this.onVisibility);
      if (this.dataset.designMode === 'true' && this.notice) {
        this.content.hidden = true;
        this.notice.textContent = message;
        this.notice.hidden = false;
        this.hidden = false;
      } else {
        this.hidden = true;
      }
    }

    tick() {
      clearTimeout(this.timeout);
      if (!this.isConnected) return;
      const remaining = this.deadline - Date.now();
      if (remaining <= 0) {
        this.hideOrExplain('Offer countdown: this deadline has passed. The block is hidden from customers. Update the deadline to use it again.');
        return;
      }
      // Without seconds, round up to the next minute so an active offer never
      // displays all zeros during its final 59 seconds.
      const totalSeconds = this.showSeconds ? Math.ceil(remaining / 1000) : Math.ceil(remaining / 60000) * 60;
      const values = {
        days: Math.floor(totalSeconds / 86400),
        hours: Math.floor(totalSeconds / 3600) % 24,
        minutes: Math.floor(totalSeconds / 60) % 60,
        seconds: totalSeconds % 60
      };
      Object.keys(this.units).forEach(unit => {
        const value = String(values[unit]).padStart(2, '0');
        if (this.units[unit].textContent !== value) this.units[unit].textContent = value;
      });
      this.hidden = false;
      if (document.hidden) return;
      const resolution = this.showSeconds ? 1000 : 60000;
      // Wake at the next displayed change and at the exact expiry, even when
      // seconds are not shown. Recalculate from the deadline to avoid drift.
      const delay = Math.min(remaining, (remaining % resolution) || resolution);
      this.timeout = setTimeout(() => this.tick(), Math.max(20, delay));
    }
  }

  customElements.define('product-offer-countdown', ProductOfferCountdown);
})();
